/**
 * The sync engine: bootstrap hydration + outbox flush, wired to auth.
 *
 * `mergeBootstrap` is the pure, unit-tested core — it takes the local
 * store and a server `BootstrapResult` and returns the merged store plus
 * whatever needs to be pushed back (the local rows that won and the
 * server doesn't already have at that timestamp). `useSync` is the thin
 * effectful shell: it drives `mergeBootstrap` off the tRPC `bootstrap`
 * query, persists the result, enqueues the push-back, and then flushes
 * the outbox on a recurring schedule.
 *
 * `updatedAt["__settings"]` is a sentinel entry in `AppState.updatedAt`
 * (which is otherwise keyed by review `itemId`) recording when the
 * learner last edited settings locally. Its absence means "settings have
 * never been edited on this device" — bootstrap's server settings are
 * then adopted outright with nothing pushed back.
 */
import { useEffect, useRef } from "react";

import { useAppAuth } from "../auth";
import { trpc } from "../trpc";

import { resolveItemKind } from "./itemKind";
import {
  clear as clearOutbox,
  enqueue,
  peek,
  type OutboxPayload,
  type SyncReviewRow,
} from "./outbox";
import type { AppState } from "./store";
import { loadState, saveState } from "./store";
import { setStreak } from "./syncStatus";

export const SETTINGS_TIMESTAMP_KEY = "__settings";

/** Mirrors `@repo/hindi-backend`'s `BootstrapResult` without importing the backend package. */
export interface BootstrapResult {
  settingsJson: string | null;
  reviews: {
    itemId: string;
    kind: string;
    ease: number;
    intervalDays: number;
    reps: number;
    lapses: number;
    dueAt: number;
    updatedAt: number;
  }[];
  lessons: { lessonId: string; completedAt: number }[];
  sessionCount: number;
  streak: number;
}

export interface MergeResult {
  merged: AppState;
  pushBack: OutboxPayload;
}

/**
 * Mirrors the backend's `syncPushInput` zod `.max()` caps (see
 * `@repo/hindi-backend`'s `router.ts`). A single `syncPush` call whose
 * payload exceeds any of these is rejected wholesale by zod (400), and the
 * whole outbox is retried verbatim on every later flush — permanently
 * wedging ALL sync (including review grades merged into the same payload)
 * once the outbox grows past the cap (e.g. weeks of daily offline use
 * queuing 20+ sessions). `takeChunk` below slices one in-bounds chunk per
 * flush instead, so a flush always has a chance to make forward progress.
 */
const MAX_REVIEWS_PER_FLUSH = 500;
const MAX_LESSONS_PER_FLUSH = 50;
const MAX_SESSIONS_PER_FLUSH = 20;

/**
 * Slice one server-cap-sized chunk off the front of an outbox payload.
 * Settings is a single row (never chunked) and always rides along on the
 * first chunk since it's small and idempotent to resend. Pure and
 * unit-tested so the "does an oversized outbox ever make progress"
 * behavior doesn't require mounting the hook or mocking tRPC.
 */
export function takeChunk(payload: OutboxPayload): OutboxPayload {
  return {
    reviews: payload.reviews.slice(0, MAX_REVIEWS_PER_FLUSH),
    lessons: payload.lessons.slice(0, MAX_LESSONS_PER_FLUSH),
    sessions: payload.sessions.slice(0, MAX_SESSIONS_PER_FLUSH),
    ...(payload.settings !== undefined ? { settings: payload.settings } : {}),
  };
}

/** Whether `payload` has more rows queued beyond what `takeChunk` would send. */
export function hasMoreAfterChunk(payload: OutboxPayload): boolean {
  return (
    payload.reviews.length > MAX_REVIEWS_PER_FLUSH ||
    payload.lessons.length > MAX_LESSONS_PER_FLUSH ||
    payload.sessions.length > MAX_SESSIONS_PER_FLUSH
  );
}

function isSettingsShape(value: unknown): value is AppState["settings"] {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (candidate["length"] === 30 || candidate["length"] === 45) &&
    typeof candidate["paperEvery"] === "number" &&
    (typeof candidate["voiceName"] === "string" ||
      candidate["voiceName"] === null) &&
    typeof candidate["rate"] === "number"
  );
}

/**
 * Merge a freshly-loaded local `AppState` with the server's `bootstrap`
 * response. Per-review LWW: the server row wins when
 * `server.updatedAt >= local.updatedAt[itemId]` (server wins ties); when
 * local wins, the item is kept as-is AND queued in `pushBack.reviews` so
 * the caller can flush it back up. Lessons union by id — anything local
 * that the server doesn't have yet is queued for push-back. `sessionCount`
 * merges by max. Settings merge by `updatedAt["__settings"]`: no local
 * timestamp means "never edited here" and the server settings (if any)
 * are adopted outright with no push-back; otherwise it's LWW same as
 * reviews.
 */
export function mergeBootstrap(
  local: AppState,
  server: BootstrapResult,
): MergeResult {
  const reviews = { ...local.reviews };
  const updatedAt = { ...local.updatedAt };
  const pushBackReviews: SyncReviewRow[] = [];

  const serverByItemId = new Map(server.reviews.map((r) => [r.itemId, r]));

  for (const row of server.reviews) {
    const localUpdatedAt = local.updatedAt[row.itemId];
    const serverWins =
      localUpdatedAt === undefined || row.updatedAt >= localUpdatedAt;
    if (serverWins) {
      reviews[row.itemId] = {
        itemId: row.itemId,
        ease: row.ease,
        intervalDays: row.intervalDays,
        reps: row.reps,
        lapses: row.lapses,
        dueAt: row.dueAt,
      };
      updatedAt[row.itemId] = row.updatedAt;
    }
  }

  // Anything local that beat (or is absent from) the server needs pushing.
  for (const [itemId, localReview] of Object.entries(local.reviews)) {
    const serverRow = serverByItemId.get(itemId);
    const localUpdatedAt = local.updatedAt[itemId];
    if (localUpdatedAt === undefined) continue;
    const localWins =
      serverRow === undefined || localUpdatedAt > serverRow.updatedAt;
    if (localWins) {
      pushBackReviews.push({
        itemId,
        kind: serverRow?.kind ?? resolveItemKind(itemId),
        ease: localReview.ease,
        intervalDays: localReview.intervalDays,
        reps: localReview.reps,
        lapses: localReview.lapses,
        dueAt: localReview.dueAt,
        updatedAt: localUpdatedAt,
      });
    }
  }

  const completedLessonIds = new Set(local.completedLessonIds);
  const serverLessonIds = new Set(server.lessons.map((l) => l.lessonId));
  for (const lessonId of serverLessonIds) completedLessonIds.add(lessonId);

  // Local-only lessons push back with Date.now() because AppState keeps no
  // per-lesson completion timestamp; the server uses completedAt only for
  // idempotent insert — streaks are derived from sessions, not lessons.
  const pushBackLessons = local.completedLessonIds
    .filter((lessonId) => !serverLessonIds.has(lessonId))
    .map((lessonId) => ({ lessonId, completedAt: Date.now() }));

  const sessionCount = Math.max(local.sessionCount, server.sessionCount);

  const localSettingsUpdatedAt = local.updatedAt[SETTINGS_TIMESTAMP_KEY];
  let settings = local.settings;
  let pushBackSettings: OutboxPayload["settings"];

  if (localSettingsUpdatedAt === undefined) {
    // Never edited locally — adopt server settings outright, if present.
    if (server.settingsJson !== null) {
      try {
        const parsed: unknown = JSON.parse(server.settingsJson);
        if (isSettingsShape(parsed)) {
          settings = parsed;
        }
      } catch {
        // Malformed server blob — keep local defaults.
      }
    }
  } else {
    // Settings were edited locally at some point. BootstrapResult only
    // exposes `settingsJson`, not the server's settings timestamp, so
    // this function can't itself prove which side is newer — it keeps
    // local (already edited) and always queues it for push-back rather
    // than guessing. Final arbitration happens server-side: `syncPush`
    // DOES have `settingsUpdatedAt` and applies the same LWW rule
    // (`lwwWins` in `@repo/hindi-backend/sync.ts`), so a stale local
    // push is simply rejected there — this can produce a redundant
    // push but never clobbers a genuinely newer server value.
    pushBackSettings = {
      json: JSON.stringify(local.settings),
      updatedAt: localSettingsUpdatedAt,
    };
  }

  const merged: AppState = {
    reviews,
    completedLessonIds: [...completedLessonIds],
    sessionCount,
    settings,
    updatedAt,
  };

  const pushBack: OutboxPayload = {
    reviews: pushBackReviews,
    lessons: pushBackLessons,
    sessions: [],
    ...(pushBackSettings !== undefined ? { settings: pushBackSettings } : {}),
  };

  return { merged, pushBack };
}

const FLUSH_INTERVAL_MS = 30_000;
const MIN_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
/**
 * Cap on consecutive backoff-scheduled retries after a run of failures
 * (e.g. no backend running at all). Without a cap, a broken/absent backend
 * makes `flushOnceRef` retry forever on its own timer, so the console
 * fills with a fresh failed-request + `TRPCClientError` pair every 5s–5min
 * for the entire session. After this many consecutive failures, the
 * backoff chain stops scheduling itself; the outbox is still retried
 * later via the normal `FLUSH_INTERVAL_MS` interval, a `requestFlush()`
 * call, or the tab going hidden — each of those resets the counter, so a
 * genuinely-recovered backend is retried again within one interval tick.
 */
const MAX_BACKOFF_ATTEMPTS = 2;

export interface BackoffState {
  /** Delay (ms) to use for the retry this state's `nextBackoffState()` call is scheduling. */
  delayMs: number;
  /** Consecutive backoff-scheduled retries so far, including this failure. */
  attempts: number;
}

export interface BackoffDecision {
  /** State to carry forward (into the retry's own eventual `nextBackoffState()` call). */
  state: BackoffState;
  /** Delay (ms) to actually schedule the upcoming retry with. */
  scheduleDelayMs: number;
}

/**
 * Pure decision core for the flush backoff chain — given the current
 * backoff state after a failed attempt, returns either the delay to
 * schedule the next retry with (5s, 10s, 20s, ... capped at
 * `MAX_BACKOFF_MS`), or `null` once `MAX_BACKOFF_ATTEMPTS` consecutive
 * retries have been exhausted and the chain should stop self-scheduling.
 *
 * Split out from `useSync` so the "does it ever stop retrying" behavior
 * (the actual bug: an absent backend used to retry forever, 5s → 5min,
 * logging a fresh failed request + `TRPCClientError` indefinitely) is
 * unit-testable without mounting the hook or mocking the tRPC client.
 */
export function nextBackoffState(
  current: BackoffState,
): BackoffDecision | null {
  const attempts = current.attempts + 1;
  if (attempts > MAX_BACKOFF_ATTEMPTS) return null;
  return {
    scheduleDelayMs: current.delayMs,
    state: {
      attempts,
      delayMs: Math.min(current.delayMs * 2, MAX_BACKOFF_MS),
    },
  };
}

/** Fresh backoff state for a new attempt cycle (success, or an externally-triggered call). */
export function initialBackoffState(): BackoffState {
  return { delayMs: MIN_BACKOFF_MS, attempts: 0 };
}

/**
 * Module-level "flush now" signal. `useSync` subscribes once; any code
 * that wants to force an immediate flush (e.g. SessionScreen right after
 * finishing a session) calls `requestFlush()`. A plain event-target-style
 * callback set is enough here — there's exactly one `useSync` instance
 * per app (mounted near the root), so this doesn't need to be a full
 * pub/sub module or React context.
 */
const flushListeners = new Set<() => void>();

export function requestFlush(): void {
  for (const listener of flushListeners) listener();
}

function subscribeFlush(listener: () => void): () => void {
  flushListeners.add(listener);
  return () => flushListeners.delete(listener);
}

/**
 * Drives bootstrap hydration + outbox flushing for the whole app.
 * Mount once near the root (inside both auth + tRPC providers).
 *
 * Bootstraps once per sign-in, merges into the local store, persists,
 * and enqueues any push-back. Then flushes the outbox: every
 * `FLUSH_INTERVAL_MS`, when the tab goes hidden, and whenever
 * `requestFlush()` is called. Failures back off exponentially
 * (5s → 5min) for up to `MAX_BACKOFF_ATTEMPTS` consecutive self-scheduled
 * retries and never drop the queue — the same `peek()` payload is retried
 * until a flush succeeds. Once that cap is hit, the backoff chain goes
 * quiet on its own (no more self-scheduled retries, no more console noise
 * from a backend that isn't coming back this session) — but every fresh,
 * externally-triggered call (the next interval tick, `requestFlush()`, or
 * tab-hidden) gets its own clean run at the full backoff chain again, so a
 * backend that comes back is still picked up within one interval tick.
 */
export function useSync(): void {
  const { mode, isSignedIn } = useAppAuth();
  const utils = trpc.useUtils();
  const syncPush = trpc.syncPush.useMutation();

  const bootstrappedRef = useRef(false);
  const flushingRef = useRef(false);
  const backoffStateRef = useRef<BackoffState>(initialBackoffState());
  const backoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushOnceRef = useRef<(isBackoffRetry?: boolean) => Promise<void>>(() =>
    Promise.resolve(),
  );
  flushOnceRef.current = async (isBackoffRetry = false) => {
    if (flushingRef.current) return;
    const fullPayload = peek();
    if (fullPayload === null) return;
    // Send at most one server-cap-sized chunk per flush (see `takeChunk`) —
    // an outbox that has grown past the caps (e.g. weeks of offline use
    // queuing 20+ sessions) must never send an oversized payload that zod
    // would reject wholesale, permanently wedging every kind of sync.
    const payload = takeChunk(fullPayload);

    // A "fresh" call (interval tick, requestFlush, tab hidden) always gets
    // its own full run at the backoff chain, even if a previous cascade
    // already exhausted MAX_BACKOFF_ATTEMPTS — only a call that IS itself
    // one of those self-scheduled retries counts against the cap.
    if (!isBackoffRetry) {
      backoffStateRef.current = initialBackoffState();
      if (backoffTimerRef.current !== null) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
    }

    flushingRef.current = true;
    try {
      await syncPush.mutateAsync(payload);
      clearOutbox(payload);
      backoffStateRef.current = initialBackoffState();
      // More queued beyond this chunk (still-oversized outbox) — drain it
      // right away rather than waiting for the next 30s interval tick.
      // `finally` below clears `flushingRef` before this fires, so the
      // recursive call isn't a same-tick reentrant no-op.
      if (hasMoreAfterChunk(fullPayload)) {
        void Promise.resolve().then(() => flushOnceRef.current());
        return;
      }
    } catch {
      // Log exactly once per backoff sequence (the first failure of a
      // fresh cascade), not on every retry — a down backend already fills
      // the console with the browser's own failed-request lines; ours
      // should explain the noise, not multiply it.
      if (backoffStateRef.current.attempts === 0) {
        console.warn(
          "[sync] flush failed — backend unreachable? Retrying briefly with backoff, then waiting for the next natural trigger.",
        );
      }
      const decision = nextBackoffState(backoffStateRef.current);
      if (decision === null) {
        // Give up self-scheduling for now — a natural trigger (interval
        // tick, requestFlush, tab hidden) will try again with a clean
        // slate via the branch above.
        return;
      }
      backoffStateRef.current = decision.state;
      backoffTimerRef.current = setTimeout(() => {
        void flushOnceRef.current(true);
      }, decision.scheduleDelayMs);
    } finally {
      flushingRef.current = false;
    }
  };

  useEffect(() => {
    if (mode === "dev" || isSignedIn) {
      if (bootstrappedRef.current) return;
      bootstrappedRef.current = true;

      void (async () => {
        let result: BootstrapResult;
        try {
          result = await utils.client.bootstrap.query({
            tzOffsetMinutes: -new Date().getTimezoneOffset(),
          });
        } catch {
          // One quiet line instead of an uncaught-in-promise stack: the
          // app is fully usable offline/local-only, so a missing backend
          // at bootstrap is a note for the dev console, not an error
          // cascade. The outbox keeps everything to push once a later
          // session bootstraps successfully.
          console.warn(
            "[sync] bootstrap failed — backend unreachable? Continuing local-only.",
          );
          return;
        }
        const local = loadState();
        const { merged, pushBack } = mergeBootstrap(local, result);
        saveState(merged);
        enqueue(pushBack);
        // Bootstrap is the only response that carries `streak` — syncPush
        // doesn't return it, and it's not reliably derivable client-side
        // from a single flush, so it's set here only.
        setStreak(result.streak);
        void flushOnceRef.current();
      })();
    }
  }, [mode, isSignedIn, utils]);

  useEffect(() => {
    const interval = setInterval(() => {
      void flushOnceRef.current();
    }, FLUSH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // A pending backoff retry must not fire after unmount (e.g. sign-out) —
  // it would call `syncPush.mutateAsync` against a torn-down mutation.
  useEffect(() => {
    return () => {
      if (backoffTimerRef.current !== null) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") {
        void flushOnceRef.current();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => subscribeFlush(() => void flushOnceRef.current()), []);
}
